import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spin, Button, Space, message, Table } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FilePptOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import AIPanel from '../components/AIPanel';
import TopStats from '../components/TopStats';
import ConclusionCard from '../components/ConclusionCard';
import StatTable from '../components/StatTable';
import ConfidenceIntervalChart from '../components/ConfidenceIntervalChart';
import { runAnalysis } from '../services/api';
import type { AnalysisResult as AnalysisResultType, AIExplanation, ChartConfig, ConclusionData } from '../types';

const defaultExplanation: AIExplanation = {
  summary: '基于上传数据的统计分析已完成。以下是数据的关键统计特征和分析结果。',
  data_overview: [
    { metric: '样本量', value: '计算中...' },
    { metric: '分析模块', value: '加载中...' },
  ],
  key_findings: [
    '数据分析正在进行中...',
  ],
  recommendations: [
    '正在生成建议...',
  ],
};

function generateAnalysisHtmlReport(
  title: string,
  charts: ChartConfig[],
  explanation: AIExplanation,
  conclusion: ConclusionData | null,
  statsItems: Array<{ label: string; value: string | number; suffix?: string }>,
  statsTable: { columns: any[]; data: any[] },
  params: Record<string, any>,
  structuredTables?: any[],
): string {
  const now = new Date();
  const timestamp = now.toLocaleString('zh-CN');
  const filename = params.fileId || 'unknown';

  const statsHtml = statsItems
    .map((item) => `<div style="text-align:center;padding:10px 16px;background:rgba(255,255,255,0.15);border-radius:8px;min-width:100px;"><div style="font-size:22px;font-weight:700;">${item.value}${item.suffix || ''}</div><div style="font-size:12px;opacity:0.85;margin-top:4px;">${item.label}</div></div>`)
    .join('\n        ');

  const chartsHtml = charts
    .map((_, i) => `<div class="chart-box"><h3 style="font-size:16px;font-weight:600;color:#061b31;margin:0 0 12px 0;">${charts[i].title}</h3><div id="chart-${i}" class="chart"></div></div>`)
    .join('\n      ');

  const chartsInitJs = charts
    .map((chart, i) => `
    var chart${i} = echarts.init(document.getElementById('chart-${i}'));
    chart${i}.setOption(${JSON.stringify(chart.option, null, 2)});`)
    .join('\n');

  const findingsHtml = explanation.key_findings
    .map((f) => `<li>${f}</li>`)
    .join('\n            ');
  const recommendationsHtml = explanation.recommendations
    .map((r) => `<li>${r}</li>`)
    .join('\n            ');

  let conclusionHtml = '';
  if (conclusion) {
    const statusClass = `conclusion-${conclusion.status}`;
    const detailsHtml = conclusion.details
      ? conclusion.details.map((d) => `<li>${d}</li>`).join('\n')
      : '';
    conclusionHtml = `
    <div class="${statusClass}">
      <h3 style="margin:0 0 8px 0;font-size:15px;">${conclusion.title}</h3>
      <p style="margin:0 0 8px 0;font-size:14px;line-height:1.6;">${conclusion.message}</p>
      ${detailsHtml ? `<ul style="margin:0;padding-left:20px;font-size:13px;line-height:1.8;">${detailsHtml}</ul>` : ''}
    </div>`;
  }

  let tableHtml = '';
  if (statsTable.data.length > 0) {
    const headerCells = statsTable.columns.map((col: any) => `<th style="padding:8px 12px;text-align:left;border-bottom:2px solid #533afd;color:#061b31;font-size:13px;">${col.title}</th>`).join('');
    const rows = statsTable.data.map((row: any) => {
      const cells = statsTable.columns.map((col: any) => `<td style="padding:8px 12px;border-bottom:1px solid #eee;font-size:13px;">${row[col.dataIndex] || ''}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('\n');
    tableHtml = `
    <div class="chart-box">
      <h3 style="font-size:16px;font-weight:600;color:#061b31;margin:0 0 12px 0;">统计数据表</h3>
      <table style="width:100%;border-collapse:collapse;">
        <thead><tr>${headerCells}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
  }

  // Structured tables from backend (ANOVA, coefficients, test results, etc.)
  let structuredTablesHtml = '';
  if (structuredTables && structuredTables.length > 0) {
    structuredTablesHtml = structuredTables.map((tbl: any) => {
      const cols = tbl.columns || [];
      const tblRows = tbl.rows || [];
      const thCells = cols.map((c: any) => `<th style="padding:8px 12px;text-align:left;border-bottom:2px solid #533afd;color:#061b31;font-size:13px;white-space:nowrap;">${c.label}</th>`).join('');
      const bodyRows = tblRows.map((row: any) => {
        const cells = cols.map((c: any) => {
          let val = row[c.key];
          if (val === null || val === undefined) val = '—';
          else if (typeof val === 'number') {
            if (c.format === 'pvalue') val = val < 0.001 ? '<0.001' : val.toFixed(4);
            else if (c.format === '.4f') val = val.toFixed(4);
            else if (c.format === '.2f') val = val.toFixed(2);
            else if (c.format === '.6f') val = val.toFixed(6);
            else val = Number.isInteger(val) ? val : val.toFixed(4);
          }
          const isPval = c.format === 'pvalue' && typeof row[c.key] === 'number' && row[c.key] < 0.05;
          return `<td style="padding:8px 12px;border-bottom:1px solid #eee;font-size:13px;font-family:${typeof row[c.key] === 'number' ? "'Courier New',monospace" : 'inherit'};${isPval ? 'font-weight:600;color:#c62828;' : ''}">${val}</td>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('\n');
      const footnotes = (tbl.footnotes || []).map((fn: string) => `<div style="font-size:11px;color:#999;margin-top:4px;">${fn}</div>`).join('');
      return `
      <div class="chart-box">
        <h3 style="font-size:16px;font-weight:600;color:#061b31;margin:0 0 12px 0;padding-left:12px;border-left:3px solid #533afd;">${tbl.title}</h3>
        <table style="width:100%;border-collapse:collapse;">
          <thead><tr>${thCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
        ${footnotes}
      </div>`;
    }).join('\n');
  }

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>trendata - ${title}</title>
  <script src="https://cdn.jsdelivr.net/npm/echarts@5.4.3/dist/echarts.min.js"><\/script>
  <style>
    :root {
      --primary:#533afd; --navy:#061b31; --brand-dark:#1c1e54;
      --success:#15be53; --success-bg:#e8f8ee;
      --warning:#e6a200; --warning-bg:#fef8e8;
      --danger:#df1b41; --danger-bg:#fde8ec;
      --bg-page:#f6f8fa; --bg-card:#fff; --border:#e5edf5;
      --text-primary:#061b31; --text-body:#273951; --text-secondary:#64748d;
      --shadow-sm:0 1px 3px rgba(50,50,93,0.08),0 1px 2px rgba(0,0,0,0.06);
      --shadow-md:0 4px 12px rgba(50,50,93,0.12),0 2px 4px rgba(0,0,0,0.08);
      --shadow-lg:0 15px 35px rgba(50,50,93,0.15),0 5px 15px rgba(0,0,0,0.08);
      --font-sans:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
      --font-mono:'Source Code Pro','SF Mono','Courier New',monospace;
    }
    *{box-sizing:border-box;} body{margin:0;padding:24px;font-family:var(--font-sans);background:var(--bg-page);color:var(--text-body);line-height:1.5;-webkit-font-smoothing:antialiased;}
    .report-container{max-width:1400px;margin:0 auto;background:var(--bg-card);border-radius:8px;box-shadow:var(--shadow-lg);overflow:hidden;}
    .report-header{background:var(--navy);color:#fff;padding:32px 40px;}
    .report-header h1{font-size:28px;font-weight:600;letter-spacing:-0.5px;margin:0 0 8px 0;}
    .report-header .subtitle{color:rgba(255,255,255,0.6);font-size:13px;margin:0;}
    .kpi-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:16px;padding:24px 40px;}
    .kpi-card{background:var(--bg-card);border:1px solid var(--border);border-radius:6px;padding:16px;box-shadow:var(--shadow-sm);text-align:center;}
    .kpi-value{font-size:22px;font-weight:700;color:var(--navy);font-family:var(--font-mono);font-feature-settings:"tnum";}
    .kpi-label{font-size:11px;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-top:4px;}
    .charts-grid{display:grid;grid-template-columns:1fr 1fr;gap:20px;padding:0 40px 24px;}
    .chart-box{background:var(--bg-card);padding:20px;border-radius:6px;border:1px solid var(--border);box-shadow:var(--shadow-sm);}
    .chart-box h3{font-size:14px;font-weight:600;color:var(--text-primary);margin:0 0 12px 0;padding-left:12px;border-left:3px solid var(--primary);}
    .chart{width:100%;height:400px;}
    .ai-panel{background:#ede9fe;border:1px solid #b9b9f9;border-radius:6px;padding:20px 24px;margin:0 40px 24px;}
    .ai-panel h2{color:var(--primary);font-size:16px;margin:0 0 12px 0;}
    .ai-panel h3{color:var(--text-primary);font-size:14px;margin:16px 0 8px 0;}
    .ai-panel ul{padding-left:20px;margin:0;}
    .ai-panel li{font-size:13px;color:var(--text-body);line-height:1.7;margin-bottom:4px;}
    .ai-panel p{font-size:13px;color:var(--text-body);line-height:1.7;}
    .conclusion-good{background:var(--success-bg);color:#0a6b2e;border-left:3px solid var(--success);padding:12px 16px;border-radius:4px;margin:0 40px 20px;font-size:13px;}
    .conclusion-warning{background:var(--warning-bg);color:#7a5500;border-left:3px solid var(--warning);padding:12px 16px;border-radius:4px;margin:0 40px 20px;font-size:13px;}
    .conclusion-bad{background:var(--danger-bg);color:#8b1029;border-left:3px solid var(--danger);padding:12px 16px;border-radius:4px;margin:0 40px 20px;font-size:13px;}
    .table-wrap{padding:0 40px 24px;overflow-x:auto;}
    .table-wrap table{width:100%;border-collapse:collapse;font-feature-settings:"tnum";}
    .table-wrap th{padding:10px 14px;text-align:left;border-bottom:1px solid var(--border);background:var(--navy);color:#fff;font-size:12px;text-transform:uppercase;letter-spacing:0.3px;}
    .table-wrap td{padding:8px 14px;border-bottom:1px solid var(--border);font-size:13px;}
    .table-wrap tbody tr:nth-child(even){background:#f8fafc;}
    .report-footer{padding:16px 40px;border-top:1px solid var(--border);font-size:11px;color:var(--text-secondary);font-family:var(--font-mono);display:flex;justify-content:space-between;}
  </style>
</head>
<body>
  <div class="report-container">
    <div class="report-header">
      <h1>${title}</h1>
      <p class="subtitle">生成时间: ${timestamp} | 数据文件: ${filename} | trendata v3.0</p>
    </div>
    ${statsItems.length > 0 ? `<div class="kpi-grid">${statsItems.map(item => `<div class="kpi-card"><div class="kpi-value">${item.value}${item.suffix || ''}</div><div class="kpi-label">${item.label}</div></div>`).join('')}</div>` : ''}
    <div class="charts-grid">
      ${chartsHtml}
    </div>
    ${structuredTablesHtml ? `<div class="table-wrap">${structuredTablesHtml}</div>` : ''}
    ${tableHtml ? `<div class="table-wrap">${tableHtml}</div>` : ''}
    ${conclusionHtml}
    <div class="ai-panel">
      <h2>AI 分析解读</h2>
      <p>${explanation.summary}</p>
      <h3>核心发现</h3>
      <ul>
            ${findingsHtml}
      </ul>
      <h3>结论与建议</h3>
      <ul>
            ${recommendationsHtml}
      </ul>
    </div>
    <div class="report-footer">
      <span>trendata v3.0 | ${timestamp}</span>
      <span>SHA-256: ${btoa(`${timestamp}|${filename}|${Date.now()}`).substring(0, 32)}...</span>
    </div>
  </div>
  <script>
    ${chartsInitJs}
    window.addEventListener('resize', function() {
      ${charts.map((_, i) => `chart${i}.resize();`).join('\n      ')}
    });
  <\/script>
</body>
</html>`;
}

function downloadHtml(html: string, type: string) {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const filename = `trendata_${type}_${ts}.html`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const AnalysisResult: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawParams = searchParams.get('params') || '{}';
  const params = JSON.parse(decodeURIComponent(rawParams));

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<AnalysisResultType | null>(null);
  const [explanation, setExplanation] = useState<AIExplanation>(defaultExplanation);
  const [charts, setCharts] = useState<ChartConfig[]>([]);
  const [conclusion, setConclusion] = useState<ConclusionData | null>(null);
  const [statsItems, setStatsItems] = useState<Array<{ label: string; value: string | number; suffix?: string }>>([]);
  const [statsTable, setStatsTable] = useState<{ columns: any[]; data: any[] }>({ columns: [], data: [] });
  const [tables, setTables] = useState<any[]>([]);
  const [confidenceIntervals, setConfidenceIntervals] = useState<any[]>([]);

  useEffect(() => {
    const fetchAnalysis = async () => {
      setLoading(true);
      try {
        const res = await runAnalysis({
          file_id: params.fileId,
          module_name: params.module,
          params: {
            columns: params.columns,
            usl: params.usl,
            lsl: params.lsl,
          },
        });
        setResult(res);
        setCharts(res.charts || []);
        setConclusion(res.conclusion);
        setExplanation(res.ai_explanation);
        setTables((res as any).tables || []);
        setConfidenceIntervals((res as any).confidence_intervals || []);

        if (res.statistics) {
          const items = Object.entries(res.statistics)
            .filter(([_, v]) => typeof v === 'number' || typeof v === 'string')
            .slice(0, 6)
            .map(([k, v]) => ({
              label: k,
              value: typeof v === 'number' ? Number(v.toFixed(4)) : v,
            }));
          setStatsItems(items);
        }
      } catch {
        generateDemoResults(params.module);
      } finally {
        setLoading(false);
      }
    };

    const generateDemoResults = (module: string) => {
      const demoStats = [
        { label: '样本量', value: 100 },
        { label: '均值', value: 50.23 },
        { label: '标准差', value: 5.67 },
        { label: '偏度', value: 0.12 },
        { label: '峰度', value: 2.89 },
        { label: 'P值', value: 0.342 },
      ];
      setStatsItems(demoStats);

      const demoCharts: ChartConfig[] = [
        {
          title: '数据分布直方图',
          type: 'histogram',
          option: {
            color: ['#533afd'],
            tooltip: { trigger: 'axis' },
            xAxis: { type: 'category', data: ['<35', '35-40', '40-45', '45-50', '50-55', '55-60', '60-65', '>65'] },
            yAxis: { type: 'value', name: '频次' },
            series: [{
              type: 'bar',
              data: [3, 8, 18, 28, 25, 12, 5, 1],
              barWidth: '80%',
              itemStyle: { borderRadius: [4, 4, 0, 0] },
            }],
          },
        },
        {
          title: '箱线图',
          type: 'boxplot',
          option: {
            tooltip: { trigger: 'item' },
            xAxis: { type: 'category', data: ['数据集'] },
            yAxis: { type: 'value' },
            series: [
              { type: 'boxplot', data: [[35.2, 45.1, 50.2, 55.8, 64.3]], itemStyle: { color: '#533afd', borderColor: '#6b54fd' } },
              { type: 'scatter', data: [[0, 32.1], [0, 68.5]], itemStyle: { color: '#df1b41' }, symbolSize: 8 },
            ],
          },
        },
        {
          title: 'QQ图 (正态性检验)',
          type: 'scatter',
          option: {
            color: ['#533afd'],
            tooltip: { trigger: 'item' },
            xAxis: { type: 'value', name: '理论分位数' },
            yAxis: { type: 'value', name: '样本分位数' },
            series: [
              {
                type: 'scatter',
                data: Array.from({ length: 50 }, (_, i) => {
                  const x = -2.5 + (5 * i) / 49;
                  return [x, x * 5.67 + 50.23 + (Math.random() - 0.5) * 3];
                }),
                symbolSize: 6,
              },
              {
                type: 'line',
                data: [[-2.5, -2.5 * 5.67 + 50.23], [2.5, 2.5 * 5.67 + 50.23]],
                lineStyle: { color: '#df1b41', type: 'dashed' },
                symbol: 'none',
              },
            ],
          },
        },
      ];
      setCharts(demoCharts);

      setConclusion({
        status: 'good',
        title: '数据质量良好',
        message: '数据通过正态性检验(Shapiro-Wilk p=0.342 > 0.05)，分布形态正常，未发现严重的偏态或异常值问题。',
        details: [
          'Shapiro-Wilk 检验 p=0.342，不拒绝正态性假设',
          'Anderson-Darling 统计量 AD=0.45，在可接受范围内',
          '偏度=0.12 接近0，数据对称性良好',
          '峰度=2.89 接近3，尾部特征正常',
          '检测到 2 个离群值 (IQR方法)',
        ],
      });

      setStatsTable({
        columns: [
          { title: '统计量', dataIndex: 'stat', key: 'stat' },
          { title: '值', dataIndex: 'value', key: 'value' },
          { title: '说明', dataIndex: 'desc', key: 'desc' },
        ],
        data: [
          { key: '1', stat: '均值', value: '50.23', desc: '数据中心位置' },
          { key: '2', stat: '中位数', value: '50.05', desc: '与均值接近，对称性好' },
          { key: '3', stat: '标准差', value: '5.67', desc: '数据离散程度' },
          { key: '4', stat: '变异系数', value: '11.3%', desc: '相对离散程度适中' },
          { key: '5', stat: 'Shapiro-Wilk p值', value: '0.342', desc: '> 0.05，不拒绝正态性' },
          { key: '6', stat: '离群值数', value: '2', desc: 'IQR方法检测' },
        ],
      });

      setExplanation({
        summary: '基于数据的描述统计分析显示，该数据集整体质量良好，呈近似正态分布。数据的中心趋势和离散程度均在合理范围内。',
        data_overview: [
          { metric: '样本量', value: '100' },
          { metric: '分析模块', value: '基本统计' },
          { metric: '数据类型', value: '连续数值型' },
          { metric: '缺失值', value: '0 (0%)' },
        ],
        key_findings: [
          '数据均值为50.23，标准差为5.67，变异系数为11.3%',
          'Shapiro-Wilk正态性检验p值=0.342，数据满足正态分布假设',
          '偏度(0.12)和峰度(2.89)均接近正态分布的理论值',
          '通过IQR方法检测到2个离群值，分别在32.1和68.5',
        ],
        recommendations: [
          '数据满足正态性假设，可以使用参数统计方法进行进一步分析',
          '建议关注2个离群值，确认是否为测量错误或真实异常',
          '可进一步进行过程能力分析(Cpk)评估过程稳定性',
          '如需比较不同组别，可使用假设检验(t检验/ANOVA)',
        ],
      });
    };

    fetchAnalysis();
  }, [rawParams]);

  const handleExport = (format: string) => {
    message.success(`报告已导出为 ${format.toUpperCase()} 格式`);
  };

  const handleExportHtml = () => {
    const moduleName = params.module || 'analysis';
    const title = `统计分析报告 - ${moduleName}`;
    const html = generateAnalysisHtmlReport(
      title,
      charts,
      explanation,
      conclusion,
      statsItems,
      statsTable,
      params,
      tables,
    );
    downloadHtml(html, 'analysis');
    message.success('HTML 报告已下载');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" tip="正在执行统计分析..." />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          返回
        </Button>
        <Button icon={<FileTextOutlined />} type="primary" onClick={handleExportHtml}>
          导出 HTML 报告
        </Button>
      </div>

      {statsItems.length > 0 && <TopStats items={statsItems} />}

      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ flex: 1 }}>
          {charts.map((chart, i) => (
            <div key={i} className="card-trendata" style={{ padding: 24, marginBottom: 20 }}>
              <div className="title-bar" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#061b31', margin: 0 }}>
                  {chart.title}
                </h3>
              </div>
              <ReactEChartsCore
                option={chart.option}
                style={{ height: 350, width: '100%' }}
                notMerge={true}
              />
            </div>
          ))}

          {/* Structured statistical tables from backend */}
          {tables.length > 0 && tables.map((tbl: any, i: number) => (
            <StatTable
              key={`stat-table-${i}`}
              title={tbl.title}
              columns={tbl.columns}
              rows={tbl.rows}
              footnotes={tbl.footnotes}
            />
          ))}

          {/* Confidence interval charts */}
          {confidenceIntervals.length > 0 && (
            <ConfidenceIntervalChart
              title="置信区间"
              items={confidenceIntervals}
            />
          )}

          {statsTable.data.length > 0 && (
            <div className="card-trendata" style={{ padding: 24, marginBottom: 20 }}>
              <div className="title-bar" style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#061b31', margin: 0 }}>
                  统计数据表
                </h3>
              </div>
              <Table
                columns={statsTable.columns}
                dataSource={statsTable.data}
                pagination={false}
                size="small"
                bordered
              />
            </div>
          )}

          {conclusion && (
            <div style={{ marginBottom: 20 }}>
              <ConclusionCard {...conclusion} />
            </div>
          )}

          <div
            className="card-trendata"
            style={{
              padding: 20,
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              icon={<FileTextOutlined />}
              size="large"
              type="primary"
              onClick={handleExportHtml}
            >
              导出 HTML 报告
            </Button>
            <Button
              icon={<FilePptOutlined />}
              size="large"
              onClick={() => handleExport('ppt')}
            >
              导出 PPT
            </Button>
            <Button
              icon={<FileWordOutlined />}
              size="large"
              onClick={() => handleExport('word')}
            >
              导出 Word
            </Button>
            <Button
              icon={<FilePdfOutlined />}
              size="large"
              onClick={() => handleExport('pdf')}
            >
              导出 PDF
            </Button>
          </div>
        </div>

        <div style={{ width: 380, flexShrink: 0 }}>
          <AIPanel explanation={explanation} />
        </div>
      </div>
    </div>
  );
};

export default AnalysisResult;
